<template>
  <q-card class="invoice-card" :style="{ 'border-color': statusColor }">
    <q-card-title class="invoice-card__title-wrapper">
      <span class="invoice-card__status" :style="{ 'background-color': statusColor }">{{ $t(quote.status) }}</span>
      <span class="invoice-card__title">
         <router-link
           v-if="quote.status !== 'draft'"
           :to="{ name: 'quote',  params: { quote: quote.id, teamId: activeTeam.id } }"
           class="router-link"
         >
           {{ $tc('quote', 1) }}
           <template v-if="quote.quote_number">&nbsp;<strong>#{{ quote.quote_number }}</strong></template>
          </router-link>

          <template v-else>
            {{ $tc('quote', 1) }}
            <template v-if="quote.quote_number">&nbsp;<strong>#{{ quote.quote_number }}</strong></template>
          </template>
      </span>
    </q-card-title>

    <q-card-main class="invoice-card__main">
      <div class="invoice-card__meta">
        <div class="invoice-card__entry">
          <h5 class="invoice-card__entry-heading">{{ $tc('customer', 1) }}</h5>
          <span class="invoice-card__entry-value">
            <router-link :to="{ name: 'customer', params: { customerId: customer.id, teamId: this.activeTeam.id } }"
                         class="router-link">
              {{ customerName }}
            </router-link>
          </span>
        </div>

        <div class="invoice-card__entry">
          <h5 class="invoice-card__entry-heading">{{ $t('valid_until') }}</h5>
          <span class="invoice-card__entry-value">{{ validUntil }}</span>
        </div>
      </div>

      <div class="invoice-card__row invoice-card__row--bottom">
        <div class="invoice-card__entry invoice-card__entry--center">
          <h5 class="invoice-card__entry-heading">{{ $t('sum_gross') }}</h5>
          <span class="invoice-card__entry-value invoice-card__entry-value--big">{{ totalGross }}</span>
        </div>
      </div>
    </q-card-main>

    <q-card-actions class="invoice-card__actions">
      <div class="col">
        <q-btn v-if="!isDraft" @click="handleView" flat dense icon="visibility"/>
        <q-btn v-if="isDraft" @click="handleEdit" flat dense icon="edit"/>
        <q-btn v-if="!isDraft" @click="handleCreateInvoice" flat dense icon="receipt"/>
        <q-btn v-if="isDraft" @click="handleRemove" flat dense icon="delete"/>
        <q-btn v-if="!isDraft" @click="handlePdfDownload" flat dense icon="cloud_download"/>
      </div>

      <div class="col">
        <div class="row justify-end">
          <q-checkbox
            v-model="selected"
            @input="$emit('select', $event)"
            :class="['invoice-card__actions__select', { 'block': selected }]"
          />
        </div>
      </div>
    </q-card-actions>
  </q-card>
</template>

<script>
  import { colors, date } from 'quasar'
  import { mapActions, mapGetters } from 'vuex'
  import FileSaver from 'file-saver'
  import { QuoteClient } from 'src/api/Quotes'
  import { formatMoney } from 'src/utils/format'
  import { getCustomerName } from 'src/utils/searchResultParser'
  import parseDate from 'date-fns/parse'
  import { quotes } from 'src/config'

  export default {
    props: {
      quote: {
        required: true,
        type: Object
      }
    },

    data () {
      return {
        selected: false
      }
    },

    computed: {
      ...mapGetters(['activeTeam']),

      statusColor () {
        return colors.getBrand(quotes.status_colors[this.quote.status])
      },

      totalGross () {
        return formatMoney(this.quote.total_gross, this.quote.currency)
      },

      customer () {
        return this.quote.customer.data
      },

      customerName () {
        return getCustomerName(this.customer)
      },

      validUntil () {
        return date.formatDate(parseDate(this.quote.valid_until.date), this.$t('date_format'))
      },

      isDraft () {
        return this.quote.status === 'draft'
      }
    },

    methods: {
      ...mapActions(['deleteQuotes']),

      handleView () {
        this.$router.push({ name: 'quote', params: { team: this.activeTeam.id, quote: this.quote.id } })
      },

      handleEdit () {
        this.$router.push({ name: 'edit-quote', params: { team: this.activeTeam.id, quote: this.quote.id } })
      },

      handleRemove () {
        this.$q.dialog({
          title: this.$tc('confirm_checked_invoices_delete_title', 1),
          message: this.$tc('confirm_checked_invoices_delete_body', 1),
          ok: this.$t('confirm'),
          cancel: this.$t('abort')
        })
          .then(() => this.deleteQuotes([this.quote]))
          // no-op
          .catch(() => {})
      },

      handlePdfDownload () {
        new QuoteClient()
          .pdf(this.quote.id)
          .then(response => {
            const blob = new Blob([response.data], { type: 'application/pdf' })
            FileSaver.saveAs(blob, this.$tc('quote', 1) + '_' + this.quote.quote_number + '.pdf')
          })
      },

      handleCreateInvoice () {
        this.$router.push({
          name: 'create-invoice',
          params: { team: this.activeTeam.id },
          query: { quote: this.quote.id }
        })
      }
    }
  }
</script>

<style lang="stylus" src="../Invoices/invoice-card.styl"></style>
